import { useId, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Megaphone,
  Paperclip,
  Trash2,
} from 'lucide-react';
import { announcementsApi } from '../api/announcements';
import type { AttachmentInput } from '../api/announcements';
import {
  ATTACHMENT_LABEL_MAX,
  ATTACHMENT_LIMIT,
  ATTACHMENT_URL_MAX,
  documentKindLabel,
  formatByteSize,
  labelFromFileName,
  linkAddressProblem,
  moveItem,
} from '../lib/attachments';
import { ComplianceRefusal } from '../api/conversations';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CuratedText } from '../ui/CuratedText';
import { SelectField, TextAreaField, TextField } from '../ui/Field';

/**
 * Publishing standing information.
 *
 * The form is deliberately short. An announcement is a headline and a paragraph that a family
 * reads at a glance; anything that needs a form with ten boxes is an event, and the product has
 * one of those.
 */
export function NewAnnouncementScreen({
  unitId,
  unitName,
  allowsYouthParticipants,
  onPublished,
  onCancel,
}: {
  unitId: string;
  unitName: string;
  /**
   * False in a Pack, whose youth are never conversation participants. There is then only one
   * audience an announcement's thread can have, so the picker is replaced by a line saying what
   * it will be — see `NewEventScreen`, which makes the same call for the same reason.
   */
  allowsYouthParticipants: boolean;
  onPublished: (announcementId: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  // Seeded from the unit, exactly as the new-event form does it. A constant here is what stopped
  // a Pack publishing at all (TROOP-37).
  const [audience, setAudience] = useState<'Everyone' | 'AdultsOnly'>(
    allowsYouthParticipants ? 'Everyone' : 'AdultsOnly',
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // What the announcement will hand out, in the order it will show them. One list, drawn and sent.
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const nextKey = useRef(0);
  const attachmentsHeading = useId();
  const fileInput = useRef<HTMLInputElement>(null);

  const uploading = attachments.some((row) => row.kind === 'Document' && !row.storedFileId);
  const atLimit = attachments.length >= ATTACHMENT_LIMIT;

  function update(key: number, change: Partial<DraftDocument> | Partial<DraftLink>) {
    setAttachments((rows) =>
      rows.map((row) => (row.key === key ? ({ ...row, ...change } as DraftAttachment) : row)),
    );
  }

  function remove(key: number) {
    setAttachments((rows) => rows.filter((row) => row.key !== key));
  }

  function move(index: number, delta: -1 | 1) {
    setAttachments((rows) => moveItem(rows, index, delta));
  }

  function addLink() {
    const key = nextKey.current++;
    setAttachments((rows) => [...rows, { key, kind: 'Link', label: '', url: '', touched: false }]);
  }

  /** Uploads the moment a file is picked, so publishing only has to name what is already there. */
  async function addDocument(file: File) {
    const key = nextKey.current++;
    setUploadError(null);
    setAttachments((rows) => [
      ...rows,
      {
        key,
        kind: 'Document',
        fileName: file.name,
        label: labelFromFileName(file.name),
        storedFileId: null,
        contentType: file.type,
        byteSize: file.size,
      },
    ]);

    try {
      const uploaded = await announcementsApi.uploadDocument(unitId, file);
      update(key, {
        storedFileId: uploaded.storedFileId,
        contentType: uploaded.contentType,
        byteSize: uploaded.byteSize,
      });
    } catch (e) {
      // The row goes, and the server's own sentence says why -- too large, the unit's allowance
      // used up, a kind of file it does not take.
      remove(key);
      setUploadError(
        `${file.name}: ${e instanceof Error ? e.message : 'That file could not be attached.'}`,
      );
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (uploading) {
      return;
    }
    setBusy(true);
    setFieldErrors({});
    setError(null);

    try {
      const published = await announcementsApi.publish(unitId, {
        title: title.trim(),
        body: body.trim(),
        isPinned,
        threadAudience: audience,
        // Composed from the rows on screen, in the order they are drawn -- never from a second
        // list kept beside them. The submit button refuses while an upload is in flight, so every
        // document row here has its id.
        attachments: attachments.flatMap((row): AttachmentInput[] =>
          row.kind === 'Link'
            ? [{ kind: 'Link', label: row.label.trim(), url: row.url.trim() }]
            : row.storedFileId
              ? [{ kind: 'Document', storedFileId: row.storedFileId, label: row.label.trim() }]
              : [],
        ),
      });
      onPublished(published.announcementId);
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(e.errors);
      } else if (e instanceof ComplianceRefusal) {
        setError(`${e.title} ${e.message}`);
      } else {
        setError(e instanceof Error ? e.message : 'Could not publish it.');
      }
      setBusy(false);
    }
  }

  const incomplete = !title.trim()
    ? 'Give it a headline first.'
    : !body.trim()
      ? 'Say what it is.'
      : uploading
        ? 'Wait for the file to finish uploading.'
        : attachments.some((row) => !row.label.trim())
          ? 'Give each attachment a label.'
          : attachments.some((row) => row.kind === 'Link' && !row.url.trim())
            ? 'Paste an address for each link.'
            : undefined;

  return (
    <Page>
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <Megaphone size={14} aria-hidden="true" />
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              Post an announcement
            </h1>
            <p
              className="t-body"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-2) 0 0',
                maxWidth: '46ch',
              }}
            >
              This sits at the top of the unit&rsquo;s page and stays there. Put the answer here,
              not in the thread underneath it.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <form onSubmit={submit} style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
              <TextField
                label="Headline"
                required
                value={title}
                maxLength={300}
                onChange={(e) => setTitle(e.currentTarget.value)}
                error={fieldErrors.title?.[0]}
                hint="What a family needs to know, in one line."
              />

              <TextAreaField
                label="What it says"
                required
                rows={6}
                value={body}
                onChange={(e) => setBody(e.currentTarget.value)}
                error={fieldErrors.body?.[0]}
                hint="The whole answer. If people have to ask in the thread, it belongs up here instead. **bold**, *italic*, a line starting with - for a list, and [words](https://...) for a link."
              />

              {/* What families will read, drawn by the card's own renderer (TROOP-255) -- so a link
                  shows here with the host beside it exactly as it will on the noticeboard, and one
                  the renderer will not draw shows as the characters typed before the server says
                  why. Only once there is something to show. */}
              {body.trim() && (
                <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
                  <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                    How it will read
                  </span>
                  <CuratedText
                    text={body}
                    style={{
                      color: 'var(--color-text-primary)',
                      background: 'var(--color-surface-raised)',
                      borderLeft: '5px solid var(--color-accent-announcement)',
                      borderRadius: '0 var(--radius-md) var(--radius-md) 0',
                      padding: 'var(--space-3) var(--space-4)',
                    }}
                  />
                </div>
              )}

              <section
                aria-labelledby={attachmentsHeading}
                className="flex flex-col"
                style={{ gap: 'var(--space-3)' }}
              >
                <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
                  <h2
                    id={attachmentsHeading}
                    className="t-label inline-flex items-center gap-2"
                    style={{ color: 'var(--color-text-muted)', margin: 0 }}
                  >
                    <Paperclip size={14} aria-hidden="true" />
                    Attachments
                    <span style={{ textTransform: 'none', letterSpacing: 0 }}> (optional)</span>
                  </h2>
                  <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                    Up to {ATTACHMENT_LIMIT} &mdash; documents or links. A document is a PDF or a
                    picture. Each one shows under the announcement, and a link shows where it
                    goes.
                  </p>
                  {/* The settled nudge: a handout is not a returned form. No control here --
                      this form does not know which event it would be. */}
                  <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                    This is for handouts and things to read. If every family has to hand something
                    back, like a signed form, add it to the event&rsquo;s Forms instead &mdash;
                    that is where you can see who has done it.
                  </p>
                </div>

                {attachments.length > 0 && (
                  <ol
                    className="flex flex-col"
                    style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--space-3)' }}
                  >
                    {attachments.map((row, index) => (
                      <DraftRow
                        key={row.key}
                        row={row}
                        position={index + 1}
                        count={attachments.length}
                        onChange={(change) => update(row.key, change)}
                        onRemove={() => remove(row.key)}
                        onMoveUp={index > 0 ? () => move(index, -1) : undefined}
                        onMoveDown={
                          index < attachments.length - 1 ? () => move(index, 1) : undefined
                        }
                      />
                    ))}
                  </ol>
                )}

                <input
                  ref={fileInput}
                  type="file"
                  accept="application/pdf,image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      void addDocument(file);
                    }
                    e.target.value = '';
                  }}
                />

                {/* Adds stop at the cap rather than greying out; what is left is the reason. */}
                {atLimit ? (
                  <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                    That is as many as one announcement can carry. Remove one to add another.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    <Button variant="secondary" onClick={() => fileInput.current?.click()}>
                      <FileText size={16} aria-hidden="true" />
                      Add a document
                    </Button>
                    <Button variant="secondary" onClick={addLink}>
                      <ExternalLink size={16} aria-hidden="true" />
                      Add a link
                    </Button>
                  </div>
                )}

                {uploadError && (
                  <p
                    className="t-caption"
                    role="alert"
                    style={{ color: 'var(--color-status-blocked)', margin: 0 }}
                  >
                    {uploadError}
                  </p>
                )}

                {fieldErrors.attachments?.[0] && (
                  <p
                    className="t-caption"
                    role="alert"
                    style={{ color: 'var(--color-status-blocked)', margin: 0 }}
                  >
                    {fieldErrors.attachments[0]}
                  </p>
                )}
              </section>

              {allowsYouthParticipants ? (
                <SelectField
                  label="Who it is for"
                  required
                  value={audience}
                  onChange={(e) => setAudience(e.currentTarget.value as 'Everyone' | 'AdultsOnly')}
                  error={fieldErrors.threadAudience?.[0]}
                  hint="Adults only keeps the thread to leaders and parents. The two-adult rule does not apply to a thread with no youth in it."
                >
                  <option value="Everyone">Everyone in the unit</option>
                  <option value="AdultsOnly">Adults only</option>
                </SelectField>
              ) : (
                <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                  This reaches {unitName}&rsquo;s leaders and every parent. Its youth are not in
                  the unit&rsquo;s conversations, so there is no narrower audience to choose.
                </p>
              )}

              <label className="flex items-start gap-3" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.currentTarget.checked)}
                  style={{ marginTop: '0.25em', width: 18, height: 18, flexShrink: 0 }}
                />
                <span>
                  <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                    Pin it to the top
                  </span>
                  <span
                    className="t-caption"
                    style={{ display: 'block', color: 'var(--color-text-secondary)' }}
                  >
                    For something that stays true for a while — recharter, summer camp deadlines.
                  </span>
                </span>
              </label>

              {error && (
                <p
                  className="t-body"
                  role="alert"
                  style={{
                    background: 'var(--color-status-blocked-surface)',
                    border: '1px solid var(--color-status-blocked)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)',
                    color: 'var(--color-text-primary)',
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button variant="primary" type="submit" disabledReason={incomplete}>
                  {busy ? 'Posting…' : 'Post it'}
                </Button>

                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/**
 * A document on the form. `storedFileId` is null while the upload is in flight, and the submit
 * button refuses until every one has arrived.
 */
interface DraftDocument {
  key: number;
  kind: 'Document';
  fileName: string;
  label: string;
  storedFileId: string | null;
  contentType: string;
  byteSize: number;
}

/** A link on the form: a label and an address, typed into their own boxes. */
interface DraftLink {
  key: number;
  kind: 'Link';
  label: string;
  url: string;
  /** The address box has been left once, so its pre-check may speak. Not before: mid-word is not wrong. */
  touched: boolean;
}

type DraftAttachment = DraftDocument | DraftLink;

/**
 * One attachment being prepared. SPEC-GAP: §6 has no list editor; built from the card surface,
 * `--color-border-subtle`, `--radius-md` and the existing `Field` boxes, with the move and remove
 * controls the program plan's rows already use. The first row has no *move up* and the last no
 * *move down* — absent rather than disabled, since there is nothing to explain.
 */
function DraftRow({
  row,
  position,
  count,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  row: DraftAttachment;
  position: number;
  count: number;
  onChange: (change: Partial<DraftDocument> | Partial<DraftLink>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const name = row.label.trim() || (row.kind === 'Document' ? row.fileName : 'link');
  const addressProblem =
    row.kind === 'Link' && row.touched && row.url.trim() ? linkAddressProblem(row.url) : null;

  return (
    <li
      aria-label={`Attachment ${position} of ${count}`}
      className="flex flex-col"
      style={{
        gap: 'var(--space-3)',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
      }}
    >
      <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
        <span
          className="t-caption inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-secondary)', flex: '1 1 8rem', minWidth: '6rem' }}
        >
          {row.kind === 'Document' ? (
            <>
              <FileText size={16} aria-hidden="true" className="shrink-0" />
              {row.storedFileId ? (
                <span>
                  {documentKindLabel(row.contentType)} &middot; {formatByteSize(row.byteSize)}
                </span>
              ) : (
                <span role="status" style={{ overflowWrap: 'anywhere' }}>
                  Uploading {row.fileName}&hellip;
                </span>
              )}
            </>
          ) : (
            <>
              <ExternalLink size={16} aria-hidden="true" className="shrink-0" />
              <span>Link</span>
            </>
          )}
        </span>

        {onMoveUp && <RowAction label={`Move ${name} up`} onClick={onMoveUp} icon={ChevronUp} />}
        {onMoveDown && (
          <RowAction label={`Move ${name} down`} onClick={onMoveDown} icon={ChevronDown} />
        )}
        <RowAction label={`Remove ${name}`} onClick={onRemove} icon={Trash2} />
      </div>

      <TextField
        label="Label"
        required
        value={row.label}
        maxLength={ATTACHMENT_LABEL_MAX}
        onChange={(e) => onChange({ label: e.currentTarget.value })}
        hint={
          row.kind === 'Document'
            ? 'What families see. The download is named from it too.'
            : 'What families see and tap.'
        }
      />

      {row.kind === 'Link' && (
        <TextField
          label="Address"
          required
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="https://"
          value={row.url}
          maxLength={ATTACHMENT_URL_MAX}
          onChange={(e) => onChange({ url: e.currentTarget.value })}
          onBlur={() => onChange({ touched: true })}
          error={addressProblem ?? undefined}
          hint="Families see the address this actually goes to, beside the label. Paste the whole thing."
        />
      )}
    </li>
  );
}

function RowAction({
  label,
  onClick,
  icon: Icon,
}: {
  label: string;
  onClick: () => void;
  icon: typeof Trash2;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md shrink-0"
      style={{
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        background: 'none',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
